/**
 * Validate a `next` redirect target.
 *
 * `next` survives the Stripe round-trip: we put it in the checkout session, the
 * user leaves the origin entirely, and it comes back as a query param on the
 * return page. Navigating to it unvalidated is an open redirect — on the payment
 * return page, which is the worst possible place for one (it's the page a user
 * is most primed to trust, right after handing over money).
 *
 * Only same-origin absolute paths are allowed. `//evil.com` is rejected: the
 * browser reads a protocol-relative URL as a full origin change.
 */

/**
 * Destinations that are never a legitimate "where were you going".
 *
 * These are the pages that GET you somewhere, so sending someone to one of them
 * after arriving is at best a no-op and at worst a cycle: /login redirects an
 * authenticated user to `dest`, and if `dest` is /login that is a loop with no
 * exit condition of its own.
 *
 * Today it self-terminates by luck — `<Navigate to="/login">` drops the query
 * string, so the second pass falls through to the fallback. That is not a
 * property to rely on: any caller that preserves the query, or any future route
 * that reads `next` from state instead, turns it into a real cycle. The site
 * has now had two redirect loops in one day; this closes the third before it
 * has somewhere to bite.
 */
const NEVER_A_DESTINATION = ["/login", "/signup", "/welcome", "/auth"];

export function safeNext(next: string | null | undefined, fallback = "/app"): string {
  if (!next) return fallback;
  /* Must start with a single slash, and must not begin a scheme or an authority. */
  if (!/^\/(?!\/)/.test(next)) return fallback;
  if (next.includes("\\")) return fallback; // some parsers treat \ as /

  /* Prefix match on a path SEGMENT, so /auth catches /auth/callback but /loginx
     — a route someone might legitimately add — is not caught by /login. */
  const path = next.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
  if (NEVER_A_DESTINATION.some((p) => path === p || path.startsWith(`${p}/`))) {
    return fallback;
  }
  return next;
}
